<script lang="ts">
  import { soundService } from '../services/soundService';
  import { soundStore, isMuted, masterVolume } from '../stores/soundStore';
  import { SoundCategory } from '../types/audio';
  import { announceToScreenReader } from '../lib/accessibility';

  let showControls = false;
  let volumeAnnounceTimeout: NodeJS.Timeout;

  function toggleMute() {
    soundStore.toggleMute();
    soundService.setMuted($isMuted);

    announceToScreenReader(
      $isMuted ? 'Audio muted' : 'Audio unmuted',
      'assertive'
    );
  }

  function handleMasterVolumeChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const volume = parseFloat(target.value);
    soundStore.setMasterVolume(volume);
    soundService.setMasterVolume(volume);

    // Debounced announcement
    clearTimeout(volumeAnnounceTimeout);
    volumeAnnounceTimeout = setTimeout(() => {
      announceToScreenReader(
        `Master volume ${Math.round(volume * 100)} percent`,
        'polite'
      );
    }, 500);
  }

  function handleCategoryVolumeChange(category: SoundCategory, event: Event) {
    const target = event.target as HTMLInputElement;
    const volume = parseFloat(target.value);
    soundStore.setCategoryVolume(category, volume);
    soundService.setCategoryVolume(category, volume);
  }

  function toggleControls() {
    showControls = !showControls;
    if (showControls) {
      announceToScreenReader('Audio settings opened', 'polite');
    }
  }

  function closeControls() {
    showControls = false;
    announceToScreenReader('Audio settings closed', 'polite');
  }
</script>

<div class="audio-controls">
  <button
    on:click={toggleMute}
    class="mute-button"
    aria-label={$isMuted ? 'Unmute audio' : 'Mute audio'}
    aria-pressed={$isMuted}
  >
    {#if $isMuted}
      🔇
    {:else}
      🔊
    {/if}
  </button>

  <button
    on:click={toggleControls}
    class="settings-button"
    aria-label="Audio settings"
    aria-expanded={showControls}
  >
    ⚙️
  </button>

  {#if showControls}
    <div class="controls-panel" role="dialog" aria-label="Audio settings">
      <h3>Audio Settings</h3>

      <div class="volume-control">
        <label for="master-volume">
          Master Volume
          <span class="volume-value">{Math.round($masterVolume * 100)}%</span>
        </label>
        <input
          id="master-volume"
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={$masterVolume}
          on:input={handleMasterVolumeChange}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={Math.round($masterVolume * 100)}
        />
      </div>

      <div class="category-controls">
        <h4>Category Volumes</h4>

        {#each Object.values(SoundCategory) as category}
          <div class="volume-control">
            <label for={`${category}-volume`}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
              <span class="volume-value">
                {Math.round(($soundStore.categoryVolumes.get(category) ?? 1) * 100)}%
              </span>
            </label>
            <input
              id={`${category}-volume`}
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={$soundStore.categoryVolumes.get(category) ?? 1}
              on:input={(e) => handleCategoryVolumeChange(category, e)}
            />
          </div>
        {/each}
      </div>

      <button on:click={closeControls} class="close-button">
        Close
      </button>
    </div>
  {/if}
</div>

<style>
  .audio-controls {
    position: fixed;
    bottom: 1rem;
    left: 1rem;
    z-index: 1000;
  }

  .mute-button,
  .settings-button {
    background: var(--color-background-alt, #2a2a2a);
    border: 2px solid var(--color-border, #444);
    border-radius: 50%;
    width: 48px;
    height: 48px;
    font-size: 1.5rem;
    cursor: pointer;
    margin-right: 0.5rem;
    transition: all 0.2s;
  }

  .mute-button:hover,
  .settings-button:hover {
    background: var(--color-primary, #4a90e2);
    transform: scale(1.1);
  }

  .mute-button:focus,
  .settings-button:focus {
    outline: 3px solid var(--color-primary, #4a90e2);
    outline-offset: 2px;
  }

  .controls-panel {
    position: absolute;
    bottom: 60px;
    left: 0;
    background: var(--color-background, #1a1a1a);
    border: 2px solid var(--color-border, #444);
    border-radius: 8px;
    padding: 1.5rem;
    min-width: 300px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  h3 {
    margin-top: 0;
    margin-bottom: 1rem;
    color: var(--color-text, #ffffff);
  }

  h4 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    color: var(--color-text-secondary, #aaaaaa);
  }

  .volume-control {
    margin-bottom: 1rem;
  }

  .volume-control label {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    color: var(--color-text, #ffffff);
  }

  .volume-value {
    color: var(--color-text-secondary, #aaaaaa);
    font-weight: 600;
  }

  input[type="range"] {
    width: 100%;
    cursor: pointer;
  }

  .close-button {
    margin-top: 1rem;
    width: 100%;
    padding: 0.5rem;
    background: var(--color-primary, #4a90e2);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
  }

  .close-button:hover {
    background: var(--color-primary-dark, #357abd);
  }

  .close-button:focus {
    outline: 3px solid var(--color-primary, #4a90e2);
    outline-offset: 2px;
  }
</style>
